import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-article-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h2>
            <p className="text-foreground-600 mb-6">该文章可能已被删除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.thumbnail,
    publisher: {
      '@type': 'Organization',
      name: '蘑菇视频网页版',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.moguvideo.com/' },
      { '@type': 'ListItem', position: 2, name: '资讯', item: 'https://www.moguvideo.com/article/mogu-video-web-version-access-guide' },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.moguvideo.com/article/${article.slug}` },
    ],
  };

  return (
    <>
      <SchemaOrg schema={articleSchema} />
      <SchemaOrg schema={breadcrumbSchema} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 min-h-screen">
        <article className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/article/mogu-video-web-version-access-guide" className="hover:text-primary-500 transition-colors whitespace-nowrap">资讯</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
            </nav>

            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.readTime} 阅读</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-background-200/70">
                <span className="text-sm text-foreground-700 font-medium">{article.author}</span>
                <span className="text-sm text-foreground-500">{article.date}</span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-eye-line mr-1"></i>
                  {article.views} 阅读
                </span>
              </div>
            </div>

            <div className="relative aspect-video rounded-xl overflow-hidden mb-10">
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full h-full object-cover object-top"
                title={article.title}
              />
            </div>

            <div className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed">
              {article.content.split('\n').map((paragraph, idx) => {
                const trimmed = paragraph.trim();
                if (!trimmed) return null;
                if (trimmed.startsWith('技巧') || trimmed.startsWith('问题') || trimmed.startsWith('总结')) {
                  return (
                    <h3 key={idx} className="text-lg md:text-xl font-bold text-foreground-950 mt-8 mb-3 font-heading">
                      {trimmed}
                    </h3>
                  );
                }
                return (
                  <p key={idx} className="mb-4 text-foreground-700">
                    {trimmed}
                  </p>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-background-200/70">
              <span className="text-sm text-foreground-500 mr-2">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 border border-background-200/70"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex items-center gap-4 mt-6 pt-4 border-t border-background-200/70">
              <span className="text-sm text-foreground-500">分享：</span>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" title="分享到微信">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" title="分享到微博">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" title="复制链接">
                <i className="ri-link"></i>
              </a>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer no-underline"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={ra.thumbnail}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={ra.title}
                      />
                    </div>
                    <div className="p-3.5">
                      <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {ra.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-foreground-500">{ra.date}</span>
                        <span className="text-xs text-foreground-500">{ra.readTime} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}